/**
 * Pure game mechanic functions — no React state, no Supabase.
 * These can be unit-tested independently.
 */

/**
 * Classify an array of word objects into skill buckets
 * using an array of word_stats rows from the DB.
 */
export const classifyWords = (baseWords, statsArray) => {
  const statsMap = Object.fromEntries(
    (statsArray || []).map(s => [s.word.toLowerCase().trim(), s])
  );

  const classification = {
    new: [],
    learning_negative: [],
    learning_positive: [],
    weak: [],
    strong: [],
    mastered: []
  };

  baseWords.forEach(w => {
    const normalizedWord = w.word.toLowerCase().trim();
    const stat = statsMap[normalizedWord];
    if (!stat || (stat.seen_count || 0) === 0) {
      classification.new.push(w.word);
    } else {
      const streak = stat.streak || 0;
      const seen = stat.seen_count || 0;
      if (streak <= -2) classification.weak.push(w.word);
      else if (streak === -1) classification.learning_negative.push(w.word);
      else if (streak === 1) classification.learning_positive.push(w.word);
      else if (streak >= 4 && seen >= 5) classification.mastered.push(w.word);
      else if (streak >= 2) classification.strong.push(w.word);
      else classification.new.push(w.word); // Default fallback
    }
  });

  return classification;
};

/**
 * Build a balanced practice session from a classified word map.
 * Returns an array of word *strings* (not full objects).
 */
export const generateMixedPractice = (classified, gameLength) => {
  const limit = gameLength === "quick" ? 10 : 20;

  const targets = gameLength === "quick" ? {
    weak: 4,
    learning_negative: 1,
    learning_positive: 1,
    new: 2,
    strong: 1,
    mastered: 1
  } : {
    weak: 5,
    learning_negative: 3,
    learning_positive: 2,
    new: 5,
    strong: 3,
    mastered: 2
  };

  const priorities = ['weak', 'learning_negative', 'new', 'learning_positive', 'strong', 'mastered'];

  let session = [];
  const used = new Set();

  const pick = (category, count) => {
    const available = (classified[category] || []).filter(w => !used.has(w));
    const shuffled = [...available].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    selected.forEach(w => {
      session.push(w);
      used.add(w);
    });
    return selected.length;
  };

  // 1. Initial targets
  Object.keys(targets).forEach(cat => {
    pick(cat, targets[cat]);
  });

  // 2. Fill remaining slots using priority order
  let remaining = limit - session.length;
  if (remaining > 0) {
    for (const cat of priorities) {
      if (remaining <= 0) break;
      const added = pick(cat, remaining);
      remaining -= added;
    }
  }

  return session.sort(() => 0.5 - Math.random());
};

/**
 * Build a review-focused session that prioritises mistakes.
 * Returns an array of word *strings*.
 */
export const generateReviewSession = (classified, gameLength) => {
  const limit = gameLength === "quick" ? 10 : 20;

  let sessionWords = [];
  const used = new Set();

  const pick = (category, count) => {
    const available = (classified[category] || []).filter(w => !used.has(w));
    const shuffled = [...available].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    selected.forEach(word => {
      sessionWords.push(word);
      used.add(word);
    });
    return selected.length;
  };

  // Priority 1: True Mistakes (Weak and Learning Negative)
  pick('weak', limit);
  pick('learning_negative', limit - sessionWords.length);

  // Priority 2: Fill remaining with other words
  const fallbacks = ['new', 'learning_positive', 'strong', 'mastered'];
  for (const cat of fallbacks) {
    const remaining = limit - sessionWords.length;
    if (remaining <= 0) break;
    pick(cat, remaining);
  }

  return sessionWords.sort(() => 0.5 - Math.random());
};

/**
 * Compute a score for a correct answer.
 * Returns { points, isSpeedy }.
 */
export const computeScore = (timeTaken, playMode, lives) => {
  let points = 50; // base

  const isSpeedy = timeTaken <= 3;

  if (timeTaken <= 3) {
    points += 30;
  } else if (timeTaken <= 5) {
    points += 15;
  }

  if (playMode === "quick") {
    points += lives * 10;
  }

  return { points, isSpeedy };
};

/**
 * Helper: get a time-of-day greeting string.
 */
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};
